import DoneIcon from '@mui/icons-material/Done';
import InfoIcon from '@mui/icons-material/Info';
import { Box, IconButton, Stack, TextField, Tooltip } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import GenericModal from 'components/GenericModal';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

/**
 * Dialog with two date pickers (start + end) for Insights.
 * - Choosing a start date snaps the end date to the same value.
 * - End-date choices are limited to `[start, start + 6 days]` and must be ≤ yesterday.
 * - On ✔ returns `{ startDate, endDate }` as `dd.MM.yyyy` strings via `onSelectionOK`.
 */
export default function InsightsDialogSelect(props) {
  /* ------------------------------------------------------------------
   *  CONSTANTS (memoised so they stay reference-stable across renders)
   * ---------------------------------------------------------------- */
  const today = useMemo(() => DateTime.local().startOf('day'), []);
  const minDate = useMemo(() => today.minus({ days: 61 }), [today]);
  const yesterday = useMemo(() => today.minus({ days: 1 }), [today]);

  /* ------------------------------------------------------------------
   *  STATE
   * ---------------------------------------------------------------- */
  const [open, setOpen] = useState(false);

  const [selectedStartDate, setSelectedStartDate] = useState(() =>
    props.selectedStartDate ? DateTime.fromFormat(props.selectedStartDate, 'dd.MM.yyyy') : null,
  );
  const [selectedEndDate, setSelectedEndDate] = useState(() =>
    props.selectedEndDate ? DateTime.fromFormat(props.selectedEndDate, 'dd.MM.yyyy') : null,
  );
  const [endDateMaxDate, setEndDateMaxDate] = useState(null);

  /* ------------------------------------------------------------------
   *  DERIVED VALUES — runs only when the start date changes
   * ---------------------------------------------------------------- */
  useEffect(() => {
    if (!selectedStartDate) {
      // reset when start date cleared
      if (selectedEndDate) setSelectedEndDate(null);
      if (endDateMaxDate) setEndDateMaxDate(null);
      return;
    }

    const candidateMax = selectedStartDate.plus({ days: 6 });
    const newMax = candidateMax < yesterday ? candidateMax : yesterday;

    if (!endDateMaxDate || !endDateMaxDate.equals(newMax)) {
      setEndDateMaxDate(newMax);
    }

    if (!selectedEndDate || selectedEndDate < selectedStartDate || selectedEndDate > newMax) {
      if (!selectedEndDate || !selectedEndDate.equals(selectedStartDate)) {
        setSelectedEndDate(selectedStartDate);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStartDate]);

  /* ------------------------------------------------------------------
   *  HANDLERS (memoised with useCallback)
   * ---------------------------------------------------------------- */
  const dispatch = useDispatch();

  const handleOpen = useCallback(() => setOpen(true), []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleOK = useCallback(() => {
    setOpen(false);
    if (!selectedStartDate || !selectedEndDate) {
      alert('Please select a date range');
      return;
    }

    dispatch(
      props.onSelectionOK({
        startDate: selectedStartDate.toFormat('dd.MM.yyyy'),
        endDate: selectedEndDate.toFormat('dd.MM.yyyy'),
      }),
    );
  }, [dispatch, props.onSelectionOK, selectedStartDate, selectedEndDate]);

  /* ------------------------------------------------------------------
   *  RENDER
   * ---------------------------------------------------------------- */
  const dialogTitleNode = (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Box component="span">{props.dialogTitle ?? ''}</Box>
      {props.toolTip && (
        <Tooltip
          title={props.toolTip}
          slotProps={{
            popper: {
              sx: { zIndex: 50020 },
            },
          }}>
          <IconButton size="small" sx={{ color: 'grey', p: 0.5 }}>
            <InfoIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  const InsightsDialogSelectContent = () => (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <DatePicker
          sx={{ mt: 1, width: 200 }}
          label="Start date"
          value={selectedStartDate}
          onChange={setSelectedStartDate}
          minDate={minDate}
          maxDate={yesterday}
          format="dd.MM.yyyy"
          slots={{ textField: TextField }}
          slotProps={{
            textField: { variant: 'outlined' },
            popper: { sx: { zIndex: 50010 } },
          }}
        />

        <DatePicker
          sx={{ mt: 1, width: 200 }}
          label="End date"
          value={selectedEndDate}
          onChange={setSelectedEndDate}
          minDate={selectedStartDate}
          maxDate={endDateMaxDate}
          disabled={!selectedStartDate}
          format="dd.MM.yyyy"
          slots={{ textField: TextField }}
          slotProps={{
            textField: { variant: 'outlined' },
            popper: { sx: { zIndex: 50010 } },
          }}
        />

        <IconButton
          aria-label="ok"
          onClick={handleOK}
          sx={{
            mt: 1,
            '&:hover': {
              backgroundColor: 'rgba(225, 225, 225, 1)',
              color: 'black',
            },
          }}>
          <DoneIcon />
        </IconButton>
      </Box>
    </LocalizationProvider>
  );

  return (
    <>
      <MDButton color="white" variant="text" onClick={handleOpen}>
        <Stack>
          <MDTypography variant="button" fontWeight="bold" color={props.color}>
            {props.buttonTitle ?? ''}
          </MDTypography>
          <MDTypography variant="button" color={props.color}>
            {props.buttonSelectedTimeRangeForDisplay ?? ''}
          </MDTypography>
        </Stack>
      </MDButton>

      <GenericModal
        open={open}
        onClose={handleClose}
        title={dialogTitleNode}
        Component={InsightsDialogSelectContent}
        contentProps={{
          dividers: true,
          sx: {
            p: 2,
            borderBottom: 'none !important',
          },
        }}
        closeOnBackdrop={true}
        closeOnEsc={true}
      />
    </>
  );
}
